import { useCallback, useContext, useState } from "react";

import { message } from "antd";
import dayjs from "dayjs";

import { StepThreeFormContext } from "../contexts/StepThreeForm";
import { GenericDataFormContext } from "../../../shared/contexts/GenericDataForm";

import {
  FilePropertiesToUpload,
  PaymentMethodDataForServices,
  PaymentMethodsAdded,
} from "../interfaces";

import { PAYMENT_METHODS } from "../../../shared/utils/methods-payment.enum";
import { useFileNameHandler } from "./useFileNameHandler";
import { FileToUpload } from "../../../shared/interfaces/services/get-payments-list-service.interface";
import { DocumentUploadServiceRequestParams } from "../../../../../../shared/interfaces/services/post-send-claim-documents-service";
import { AuthContext } from "../../../../../../shared/contexts";
import { DocumentClass } from "../../../../../../shared/interfaces/enums/document-class-enum";
import { AxiosResponse } from "axios";

export const useAddPaymentHandler = () => {
  const [fileSelected, setFileSelected] = useState<any[]>([]);

  const {
    stepThreePaymentMethodForm,
    paymentMethodsDataTable,
    banksOptions,
    allBanksOptions,
    postPaymentMethod,
    postUploadDocumentation,
    paymentMethodSequence,
    setPaymentMethodSequence,
    depositBankNumber,
    setDepositBankNumber,
    depositIntegratedCollectionNumber,
    setDepositIntegratedCollectionNumber,
    paymentsToLoad,
    setPaymentsToLoad,
    setHasChanges,
    filesToSend,
    setFilesToSend
  } = useContext(StepThreeFormContext);

  const authContext = useContext(AuthContext);

  const { preliquidationInfo, setCanShowSpinner } = useContext(
    GenericDataFormContext
  );

  const { setFileNameByPaymentMethod } = useFileNameHandler();

  /* Esta función permite darle formato al documento */
  const transformFileSelected = (
    fileSelected: FileToUpload,
    properties: Record<string, string>,
    newPaymentMethod: number
  ): DocumentUploadServiceRequestParams => {
    const { base, mediaType, name } = fileSelected;
    const base64 = base.split(",")[1];

    const propertyString = Object.entries(properties).map(([key, value]) => ({
      id: key,
      type: "String" as "String",
      value: value,
    }));

    const userName =
      authContext?.getUserInfo()?.intermediarioPrincipal?.nombreInter;

    const requestParams: DocumentUploadServiceRequestParams = {
      uploadDocument: {
        documentClass: DocumentClass.PaymentReceiptTransferPreliq,
        fileName: name,
        fileType: mediaType,
        base64: base64,
        applicationId: "WebProductores2.0",
        author: userName!,
        attributes: [
          {
            id: "cmis:objectTypeId",
            type: "String",
            value: DocumentClass.PaymentReceiptTransferPreliq,
          },
          { id: "DocumentTitle", type: "String", value: name },
          { id: "cmis:name", type: "String", value: name },
        ],
      },
    };

    return requestParams;
  };

  const validatePaymentMethod = (newPaymentMethod: number) => {
    if (newPaymentMethod === PAYMENT_METHODS.ROUNDING) {
      const alreadyExistRounding = paymentMethodsDataTable.find(
        (pay: PaymentMethodsAdded) =>
          pay.codePaymentMethod === PAYMENT_METHODS.ROUNDING
      );

      if (alreadyExistRounding) {
        message.error(
          'Solo se puede agregar una forma de pago de tipo "Redondeo"'
        );

        return false;
      }
    }

    return true;
  };

  /* Esta función permite darle formato a los parámetros dentro del documento (luego se usará en la función transformFileSelected()) */
  const prepareFileProperties = (props: FilePropertiesToUpload) => {
    const preliquidationNumber = preliquidationInfo?.numeroPreliquidacion!;
    return {
      DocumentTitle: props.fileSelectedName,
      "cmis:name": props.fileSelectedName,
      "cmis:Preliquidacion": preliquidationNumber.toString(),
      ...(props.newPaymentMethod === PAYMENT_METHODS.ELECTRONIC_CHECK && {
        "cmis:Banco": props.bankSelectedName,
        "cmis:FechaTransferencia": props.checkDate,
        "cmis:NumeroCheque": props.checkNumber,
      }),
      ...(props.newPaymentMethod === PAYMENT_METHODS.TAX_WITHHOLDING && {
        "cmis:SecuenciaRetencion": props.sequence.toString(),
      }),
      ...(props.newPaymentMethod === PAYMENT_METHODS.BANK_DEPOSIT && {
        "cmis:Banco": props.bankSelectedName,
        "cmis:NroControl": depositBankNumber.toString(),
        "cmis:FechaTransferencia": props.transferDate,
      }),
    };
  };

  const processPaymentMethod = async (
    newPaymentMethod: number,
    basePaymentMethodData: PaymentMethodDataForServices,
    depositBankNumber: number,
    depositIntegratedCollectionNumber: number
  ): Promise<AxiosResponse<any>>  => {
    if (newPaymentMethod === PAYMENT_METHODS.BANK_DEPOSIT) {
      return postPaymentMethod(newPaymentMethod, {
        ...basePaymentMethodData,
        numeroDeposito: depositBankNumber.toString(),
      });
    }

    if (newPaymentMethod === PAYMENT_METHODS.GALICIA_INTEGRATED_COLLECTION) {
      return postPaymentMethod(newPaymentMethod, {
        ...basePaymentMethodData,
        numeroDeposito: depositIntegratedCollectionNumber.toString(),
      });
    }

    return postPaymentMethod(newPaymentMethod, basePaymentMethodData);
  };

  const addSequenceNumbers = () => {
    const updatedSequence = paymentMethodSequence + 1;
    setPaymentMethodSequence(updatedSequence);

    if (newPaymentMethod === PAYMENT_METHODS.BANK_DEPOSIT) {
      const updatedDepositBankNumber = depositBankNumber + 1;
      setDepositBankNumber(updatedDepositBankNumber);
    }

    if (newPaymentMethod === PAYMENT_METHODS.GALICIA_INTEGRATED_COLLECTION) {
      const updatedDepositIntegratedCollectionNumber =
        depositIntegratedCollectionNumber + 1;
      setDepositIntegratedCollectionNumber(
        updatedDepositIntegratedCollectionNumber
      );
    }
  };

  let newPaymentMethod = Number(
    stepThreePaymentMethodForm?.getFieldValue("paymentMethod")
  );

  let exactAmount = stepThreePaymentMethodForm?.getFieldValue("exactAmount");

  const currentPaymentMethod = Number(stepThreePaymentMethodForm?.getFieldValue("paymentMethod"));
  const isElectronicCheck = currentPaymentMethod === 5; // PAYMENT_METHODS.ELECTRONIC_CHECK
  
  const bankOptionsToUse = isElectronicCheck ? allBanksOptions : banksOptions;
  
  const bankSelected =
    bankOptionsToUse?.find(
      (el: any) => el.value === stepThreePaymentMethodForm?.getFieldValue("bank")
    )?.value ?? "";

  const bankSelectedName: string =
    bankOptionsToUse?.find(
      (el: any) => el.value === stepThreePaymentMethodForm?.getFieldValue("bank")
    )?.label ?? "";

  const transferDate =
    dayjs(stepThreePaymentMethodForm?.getFieldValue("transferDate")).format(
      "YYYY-MM-DD"
    ) || "-";
  const checkDate =
    dayjs(stepThreePaymentMethodForm?.getFieldValue("checkDate")).format(
      "YYYY-MM-DD"
    ) || "-";
  const checkNumber = stepThreePaymentMethodForm
    ?.getFieldValue("checkNumber")
    ?.toString();
  const observations =
    stepThreePaymentMethodForm?.getFieldValue("observations");
  let proofOfPayment =
    fileSelected.length && setFileNameByPaymentMethod(fileSelected);
  const fileSelectedName = fileSelected?.[0]?.name;

  const addPaymentSelected = useCallback(async () => {
    try {
      const validation = validatePaymentMethod(newPaymentMethod);

      if (validation) {
        setCanShowSpinner(true);
        const properties = prepareFileProperties({
          fileSelectedName,
          newPaymentMethod,
          fileSelected,
          bankSelectedName,
          transferDate,
          checkDate,
          checkNumber,
          sequence: paymentMethodSequence,
          depositBankNumber,
        });

        const basePaymentMethodData = {
          codigoBanco: bankSelected,
          fechaDeposito: transferDate,
          fecha: checkDate,
          monto: exactAmount.toString(),
          numeroCheque: checkNumber,
          observaciones: observations ? observations : "",
          archivo: proofOfPayment.toString(),
          secuencia: paymentMethodSequence.toString(),
          numeroDeposito: "",
        };
        setHasChanges(true);

        const fileTransformed = fileSelected[0]
          ? transformFileSelected(fileSelected[0], properties, newPaymentMethod)
          : null;

        setPaymentsToLoad((prev) => [
          ...prev,
          {
            newPaymentMethod,
            basePaymentMethodData,
            depositBankNumber,
            depositIntegratedCollectionNumber,
            fileToSend: fileTransformed,
          },
        ]);

        if (fileTransformed) {
          setFilesToSend((prev) => [...prev, fileTransformed]);
        }

        addSequenceNumbers();

        stepThreePaymentMethodForm?.resetFields([
          "exactAmount",
          "bank",
          "transferDate",
          "checkNumber",
          "checkDate",
          "observations",
          "proofOfPayment",
          "paymentMethod",
        ]);

        setFileSelected([]);
      }
    } catch (error) {
    } finally {
      
      setCanShowSpinner(false);
    }
  }, [
    validatePaymentMethod,
    newPaymentMethod,
    setCanShowSpinner,
    prepareFileProperties,
    fileSelectedName,
    fileSelected,
    bankSelectedName,
    transferDate,
    checkDate,
    checkNumber,
    paymentMethodSequence,
    depositBankNumber,
    bankSelected,
    exactAmount,
    observations,
    proofOfPayment,
    transformFileSelected,
    depositIntegratedCollectionNumber,
    addSequenceNumbers,
    stepThreePaymentMethodForm,
    setHasChanges
  ]);

  return {
    fileSelected,
    setFileSelected,
    addPaymentSelected,
    processPaymentMethod,
    addSequenceNumbers,
  };
};
